"use client";

import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./dialog.module.css";

export interface DialogProps {
  /** Controls visibility. */
  open: boolean;
  /** Called when the user requests to close (close button, ESC, backdrop click if dismissible). */
  onClose: () => void;
  /** Header title. If omitted, no header is rendered. */
  title?: ReactNode;
  /** Optional small subtitle rendered under the title. Ignored when falsy. */
  subtitle?: ReactNode;
  /** Body content. Ignored when `loading` is true. */
  children?: ReactNode;
  /**
   * Optional bar rendered directly under the header and *outside* the
   * scrolling body, so it stays pinned while the body scrolls beneath it —
   * no `position: sticky` or padding-offset math needed. Pass `undefined`
   * (not a component that renders null) when there's nothing to show, or the
   * bordered wrapper would render as an empty strip.
   */
  toolbar?: ReactNode;
  /** Preferred width (e.g. 600 or "600px"). Defaults to 640px. */
  width?: number | string;
  /** Maximum width (e.g. "90vw"). Defaults to "min(90vw, 720px)". */
  maxWidth?: number | string;
  /** When true, clicking the backdrop closes the dialog. Default true. */
  closeOnBackdropClick?: boolean;
  /** When true, pressing ESC closes the dialog. Default true. */
  closeOnEsc?: boolean;
  /** When true, body shows a skeleton placeholder instead of children. */
  loading?: boolean;
  /** Footer content (typically action buttons). If omitted, no footer is rendered. */
  footer?: ReactNode;
  /** Show the close (X) button in the header. Default true. */
  showCloseButton?: boolean;
  /** Optional extra class on the dialog container. */
  className?: string;
  /** ARIA label when no visible title is provided. */
  ariaLabel?: string;
}

function toCssSize(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

function DialogSkeleton() {
  return (
    <div className={styles.skeletonWrap} aria-hidden="true">
      <div className={styles.skeletonImg}></div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}></div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`}></div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}></div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}></div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}></div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`}></div>
    </div>
  );
}

export default function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  toolbar,
  width = 640,
  maxWidth = "min(90vw, 720px)",
  closeOnBackdropClick = true,
  closeOnEsc = true,
  loading = false,
  footer,
  showCloseButton = true,
  className,
  ariaLabel,
}: DialogProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // ESC to close + body scroll lock while open
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEsc) onClose();
    };
    document.addEventListener("keydown", onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, closeOnEsc, onClose]);

  if (!mounted || !open) return null;

  const dialog = (
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && closeOnBackdropClick) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : ariaLabel}
        className={`${styles.dialog} ${className || ""}`.trim()}
        style={{
          width: toCssSize(width),
          maxWidth: toCssSize(maxWidth),
        }}
      >
        {title !== undefined && (
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <h3 className={styles.title}>{title}</h3>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
            {showCloseButton && (
              <button
                type="button"
                aria-label="Close"
                className={styles.closeBtn}
                onClick={onClose}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>
        )}

        {toolbar && <div className={styles.toolbar}>{toolbar}</div>}

        <div
          className={`${styles.body} ${title === undefined && !toolbar ? styles.bodyNoHeader : ""}`.trim()}
        >
          {loading ? <DialogSkeleton /> : children}
        </div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
